import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuChevronLeft,
  LuChevronRight,
  LuRefreshCw,
  LuSearch,
  LuTruck,
  LuUserPlus,
  LuX,
} from "react-icons/lu";
import {
  assignLogisticsDedicatedServices,
  extractApiMessage,
  fetchInviteLogisticsProviders,
  getInviteLogisticsProvider,
} from "../../services/inviteLogisticsProviders.service";
import CreateLogisticsProviderInviteModal from "../../components/logistics/CreateLogisticsProviderInviteModal";
import "../../assets/styles/super_admin_access.scss";
import "../../assets/styles/invite_logistics_providers_admin.scss";

const DEBOUNCE_MS = 400;
const PAGE_OPTIONS = [10, 20, 50];

const SERVICE_OPTIONS = [
  { value: "business_relocation", label: "Business Relocation" },
  { value: "exhibition_logistics", label: "Exhibition Logistics" },
];

const ASSIGNMENT_FILTER_OPTIONS = [
  { value: "all", label: "All providers" },
  { value: "none", label: "No services assigned" },
  { value: "business_relocation", label: "Business Relocation assigned" },
  { value: "exhibition_logistics", label: "Exhibition Logistics assigned" },
  { value: "both", label: "Both services assigned" },
];

function serviceLabel(value) {
  return SERVICE_OPTIONS.find((o) => o.value === value)?.label || value;
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 7) {
  if (totalPages <= 1) return [1];
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function InviteHeroArt() {
  return (
    <svg className="brrq-admin-hero__svg" viewBox="0 0 320 120" fill="none" aria-hidden>
      <rect width="320" height="120" fill="#f1f5f9" />
      <rect x="40" y="48" width="120" height="52" rx="6" fill="#ff6a00" opacity="0.9" />
      <rect x="180" y="56" width="100" height="36" rx="4" fill="#94a3b8" />
      <circle cx="72" cy="102" r="10" fill="#334155" />
      <circle cx="132" cy="102" r="10" fill="#334155" />
      <path d="M200 40h48v24h-48z" fill="#cbd5e1" />
    </svg>
  );
}

function SortableTh({ label, column, sortBy, sortOrder, onSort }) {
  const active = sortBy === column;
  const arrow = active ? (sortOrder === "asc" ? " ↑" : " ↓") : "";
  return (
    <th scope="col">
      <button
        type="button"
        className="sa-invite-lp__sort-btn"
        onClick={() => onSort(column)}
        aria-sort={active ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}
        {arrow}
      </button>
    </th>
  );
}

export default function InviteLogisticsProvidersPage({
  basePath = "/dashboard/logistics-manager",
}) {
  const dashboardPath =
    basePath === "/dashboard/logistics-manager"
      ? "/dashboard/logistics-manager/business-relocation-rfqs"
      : "/super-admin/dashboard";
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("companyName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [serviceTypes, setServiceTypes] = useState({
    business_relocation: false,
    exhibition_logistics: false,
  });
  const [assigning, setAssigning] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [hasEngagedSearch, setHasEngagedSearch] = useState(false);
  const fetchSeqRef = useRef(0);

  const shouldFetchList =
    debouncedQuery.length > 0 || hasEngagedSearch || assignmentFilter !== "all";

  const selectedProvider = useMemo(
    () => providers.find((p) => p.companyId === selectedCompanyId) || null,
    [providers, selectedCompanyId],
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      setHasEngagedSearch(true);
    }
  }, [debouncedQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, assignmentFilter, sortBy, sortOrder, pageSize]);

  const loadList = useCallback(async () => {
    if (!shouldFetchList) {
      setLoading(false);
      return;
    }
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const data = await fetchInviteLogisticsProviders({
        q: debouncedQuery,
        page,
        pageSize,
        sortBy,
        sortOrder,
        assignmentFilter,
      });
      if (seq !== fetchSeqRef.current) return;
      setProviders(data.providers);
      setTotal(data.total);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      toast.error(extractApiMessage(err, "Could not load logistics providers."));
      setProviders([]);
      setTotal(0);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [debouncedQuery, page, pageSize, sortBy, sortOrder, assignmentFilter, shouldFetchList]);

  useEffect(() => {
    if (!shouldFetchList) {
      setLoading(false);
      setProviders([]);
      setTotal(0);
      return;
    }
    void loadList();
  }, [shouldFetchList, loadList]);

  const selectProvider = useCallback((provider) => {
    if (provider?.canAssignServices === false) {
      toast.info(
        provider.assignServicesBlockReason ||
          "This provider must activate their account and complete their profile before services can be assigned.",
      );
      return;
    }
    setSelectedCompanyId(provider.companyId);
    const assigned = provider.assignedServiceTypes || [];
    setServiceTypes({
      business_relocation: assigned.includes("business_relocation"),
      exhibition_logistics: assigned.includes("exhibition_logistics"),
    });
  }, []);

  const closeAssignModal = useCallback(() => {
    setSelectedCompanyId(null);
    setServiceTypes({
      business_relocation: false,
      exhibition_logistics: false,
    });
  }, []);

  const handleProviderCreated = useCallback(
    (result) => {
      const provider = result?.provider;
      if (!provider?.companyId) return;
      const enriched = {
        ...provider,
        canAssignServices: false,
        profileCompleted: false,
        invitationStatus: "pending_activation",
        assignServicesBlockReason:
          "This provider must activate their account via the invitation email before services can be assigned.",
        assignedServiceTypes: provider.assignedServiceTypes || [],
      };
      setProviders((prev) => {
        const exists = prev.some((p) => p.companyId === enriched.companyId);
        if (exists) {
          return prev.map((p) => (p.companyId === enriched.companyId ? { ...p, ...enriched } : p));
        }
        return [enriched, ...prev];
      });
      setTotal((t) => t + 1);
      setHasEngagedSearch(true);
      closeAssignModal();
    },
    [closeAssignModal],
  );

  const loadProviderById = useCallback(
    async (companyId) => {
      try {
        const provider = await getInviteLogisticsProvider(companyId);
        if (!provider) return;
        setProviders((prev) => {
          const exists = prev.some((p) => p.companyId === provider.companyId);
          if (exists) {
            return prev.map((p) => (p.companyId === provider.companyId ? provider : p));
          }
          return [provider, ...prev];
        });
        selectProvider(provider);
        setHasEngagedSearch(true);
      } catch {
        /* ignore */
      }
    },
    [selectProvider],
  );

  useEffect(() => {
    const raw = searchParams.get("companyId");
    const cid = raw ? Math.trunc(Number(raw)) : 0;
    if (cid > 0) {
      void loadProviderById(cid);
      const next = new URLSearchParams(searchParams);
      next.delete("companyId");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, loadProviderById]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "SA_LOGISTICS_PROVIDER_READY") return;
      const cid = Math.trunc(Number(event.data.companyId));
      if (cid > 0) {
        toast.success("New logistics provider profile saved. Select service types below.");
        void loadProviderById(cid);
        void loadList();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [loadProviderById, loadList]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  useEffect(() => {
    if (!selectedProvider) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeAssignModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedProvider, closeAssignModal]);

  const toggleService = (key) => {
    setServiceTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const chosenServiceTypes = useMemo(
    () => SERVICE_OPTIONS.map((o) => o.value).filter((key) => serviceTypes[key]),
    [serviceTypes],
  );

  const handleAssign = async () => {
    if (!selectedCompanyId) {
      toast.error("Select a logistics provider from the results.");
      return;
    }
    if (selectedProvider?.canAssignServices === false) {
      toast.info(
        selectedProvider.assignServicesBlockReason ||
          "This provider must activate their account and complete their profile before services can be assigned.",
      );
      return;
    }
    if (!chosenServiceTypes.length) {
      toast.error("Select at least one service type.");
      return;
    }
    const already = selectedProvider?.assignedServiceTypes || [];
    const toAssign = chosenServiceTypes.filter((st) => !already.includes(st));
    if (!toAssign.length) {
      toast.warning("This provider is already assigned for the selected service type(s).");
      return;
    }
    setAssigning(true);
    try {
      const result = await assignLogisticsDedicatedServices(selectedCompanyId, toAssign);
      const assigned = result?.assigned || [];
      const duplicates = result?.duplicates || [];
      if (assigned.length) {
        toast.success(
          duplicates.length
            ? `Assigned ${assigned.map(serviceLabel).join(", ")}. Some selections were already assigned.`
            : "Service type(s) assigned successfully.",
        );
      } else if (duplicates.length) {
        toast.warning("This provider is already assigned for the selected service type(s).");
      }
      await loadList();
      const refreshed = await getInviteLogisticsProvider(selectedCompanyId);
      if (refreshed) selectProvider(refreshed);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.warning(extractApiMessage(err, "This assignment already exists."));
      } else {
        toast.error(extractApiMessage(err, "Could not assign services."));
      }
    } finally {
      setAssigning(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const pageNumbers = buildPageNumbers(page, totalPages);
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const showEmptyState = shouldFetchList && !loading && total === 0;
  const listReady = shouldFetchList && !loading && total > 0;
  const showInitialHint = !shouldFetchList && !loading;

  return (
    <div className="sa-access-page sa-dispute-page brrq-admin-page sa-invite-lp">
      <header className="brrq-admin-hero">
        <div className="brrq-admin-hero__main">
          <nav className="sa-access-breadcrumb" aria-label="Breadcrumb">
            <Link to={dashboardPath}>Dashboard</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">Invite Logistics Providers</span>
          </nav>
          <h1>Invite Logistics Providers</h1>
          <p>
            Search existing logistics providers and assign them to Business Relocation and/or Exhibition
            Logistics dedicated services.
          </p>
        </div>
        <div className="brrq-admin-hero__art">
          <InviteHeroArt />
        </div>
      </header>

      <div className="sa-invite-lp__panel">
        <div
          className="sa-access-toolbar sa-dispute-toolbar brrq-admin-filters sa-invite-lp__toolbar"
          style={{ gridTemplateColumns: "minmax(220px, 1.6fr) minmax(160px, 1fr) auto" }}
        >
          <label className="sa-access-search sa-invite-lp__search-wrap">
            <LuSearch aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by company name, contact person, or email"
              aria-label="Search logistics providers"
              autoComplete="off"
            />
            {loading ? (
              <LuRefreshCw className="sa-invite-lp__search-spinner sa-invite-lp__spin" aria-hidden />
            ) : null}
          </label>
          <select
            value={assignmentFilter}
            onChange={(e) => {
              const value = e.target.value;
              setAssignmentFilter(value);
              if (value !== "all") setHasEngagedSearch(true);
            }}
            aria-label="Filter by assigned services"
          >
            {ASSIGNMENT_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="sa-access-btn sa-access-btn--primary"
            onClick={() => setCreateModalOpen(true)}
          >
            <LuUserPlus aria-hidden /> Create Logistics Provider
          </button>
        </div>

        {showInitialHint ? (
          <p className="sa-invite-lp__hint sa-invite-lp__initial-hint">
            Start typing in the search box to find logistics providers by company name, contact person, or
            email. You can also use the assignment filter to browse providers.
          </p>
        ) : null}

        {listReady ? (
          <div className="brrq-admin-card" style={{ marginTop: "1rem" }}>
            <div
              className={`sa-access-table-wrap sa-invite-lp__table-wrap${loading ? " sa-invite-lp__table-wrap--loading" : ""}`}
            >
              <table className="sa-access-table brrq-admin-table sa-invite-lp__table">
              <thead>
                <tr>
                  <SortableTh
                    label="Company"
                    column="companyName"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Contact person"
                    column="contactPerson"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTh
                    label="Email"
                    column="email"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th scope="col">Assigned services</th>
                  <th scope="col" className="sa-invite-lp__col-action">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {providers.map((p) => (
                  <tr
                    key={p.companyId}
                    className={[
                      "sa-invite-lp__row--clickable",
                      selectedCompanyId === p.companyId ? "sa-invite-lp__row--selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      if (p.canAssignServices === false) {
                        toast.info(
                          p.assignServicesBlockReason ||
                            "This provider must activate their account and complete their profile before services can be assigned.",
                        );
                        return;
                      }
                      selectProvider(p);
                    }}
                  >
                    <td>{p.companyName || "—"}</td>
                    <td>{p.contactPerson || "—"}</td>
                    <td>{p.email || "—"}</td>
                    <td>
                      <div className="sa-invite-lp__badges">
                        {(p.assignedServiceTypes || []).length ? (
                          p.assignedServiceTypes.map((st) => (
                            <span
                              key={st}
                              className={`sa-invite-lp__badge ${
                                st === "business_relocation" ? "sa-invite-lp__badge--br" : "sa-invite-lp__badge--ex"
                              }`}
                            >
                              {serviceLabel(st)}
                            </span>
                          ))
                        ) : (
                          <span className="sa-invite-lp__badge">None</span>
                        )}
                      </div>
                    </td>
                    <td className="sa-invite-lp__col-action">
                      <button
                        type="button"
                        className="sa-access-btn sa-access-btn--ghost sa-invite-lp__assign-btn"
                        disabled={p.canAssignServices === false}
                        title={p.canAssignServices === false ? p.assignServicesBlockReason || "" : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectProvider(p);
                        }}
                      >
                        Assign services
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <footer className="brrq-admin-footer">
              <span>
                Showing {rangeStart} to {rangeEnd} of {total.toLocaleString()}
              </span>
              <div className="brrq-admin-footer__center">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  aria-label="Rows per page"
                  disabled={loading}
                >
                  {PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </div>
              <div className="brrq-admin-footer__end">
                <div className="sa-access-pagination">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <LuChevronLeft aria-hidden />
                  </button>
                  {pageNumbers.map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={n === page ? "is-active" : ""}
                      disabled={loading}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                  >
                    <LuChevronRight aria-hidden />
                  </button>
                </div>
              </div>
            </footer>
          </div>
        ) : null}

        {loading && !listReady ? (
          <div className="sa-invite-lp__loading" role="status" aria-live="polite">
            <LuRefreshCw className="sa-invite-lp__spin" aria-hidden />
            <span>Loading logistics providers…</span>
          </div>
        ) : null}

        {showEmptyState ? (
          <div className="sa-invite-lp__empty">
            <p>
              <strong>No logistics providers found. Create a new logistics provider.</strong>
            </p>
            <div className="sa-invite-lp__actions" style={{ justifyContent: "center", marginTop: "1rem" }}>
              <button
                type="button"
                className="sa-access-btn sa-access-btn--primary"
                onClick={() => setCreateModalOpen(true)}
              >
                <LuUserPlus aria-hidden /> Create Logistics Provider
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {selectedProvider
        ? createPortal(
            <div
              className="sa-access-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="invite-lp-assign-title"
              onClick={closeAssignModal}
            >
              <div
                className="sa-access-modal__panel sa-dispute-assign-modal sa-invite-lp__modal"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="sa-dispute-assign-modal__head">
                  <div className="sa-dispute-assign-modal__title-wrap">
                    <span
                      className="sa-dispute-assign-modal__icon sa-dispute-assign-modal__icon--assign"
                      aria-hidden
                    >
                      <LuTruck />
                    </span>
                    <div className="sa-dispute-modal__head-main">
                      <h2 id="invite-lp-assign-title">Assign services</h2>
                      <p className="sa-dispute-assign-modal__lead">
                        Choose dedicated logistics service types for this provider.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sa-dispute-icon-btn"
                    aria-label="Close"
                    onClick={closeAssignModal}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sa-dispute-assign-modal__body">
                  <div className="sa-dispute-assign-modal__summary">
                    <div className="sa-dispute-assign-modal__summary-top">
                      <strong>{selectedProvider.companyName}</strong>
                    </div>
                    {(selectedProvider.contactPerson || selectedProvider.email) && (
                      <p>
                        {[selectedProvider.contactPerson, selectedProvider.email]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    )}
                  </div>

                  <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                    {selectedProvider.canAssignServices === false &&
                    selectedProvider.assignServicesBlockReason ? (
                      <strong>{selectedProvider.assignServicesBlockReason}</strong>
                    ) : (
                      <>
                        Already-assigned types are shown checked. Assigning the same type again will
                        show a validation message.
                      </>
                    )}
                  </p>

                  <div className="sa-invite-lp__checks sa-invite-lp__checks--modal">
                    {SERVICE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="sa-invite-lp__check-label">
                        <input
                          type="checkbox"
                          checked={Boolean(serviceTypes[opt.value])}
                          onChange={() => toggleService(opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <footer className="sa-dispute-assign-modal__footer">
                  <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={closeAssignModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--primary"
                    disabled={
                      assigning ||
                      !chosenServiceTypes.length ||
                      selectedProvider.canAssignServices === false
                    }
                    onClick={() => void handleAssign()}
                  >
                    Assign Services
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}

      <CreateLogisticsProviderInviteModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={handleProviderCreated}
      />
    </div>
  );
}
